import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { ArrowLeft, Edit, Save, X, Plus, Trash2 } from "lucide-react";

interface DelegationDetail {
  id: string;
  delegationName: string;
  organization: string;
  fromDate: string;
  toDate: string;
  budget: string;
  program: string;
  notes: string;
  purpose: string;
  country: string;
  otherPurpose: string;
}

interface DelegationMember {
  id: string;
  stt: number;
  fullName: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  passportNumber: string;
  expirationDate: string;
  address: string;
  nationality: string;
  organization: string;
  role: string;
  department: string;
}

const mockDelegationDetail: DelegationDetail = {
  id: "1",
  delegationName: "",
  organization: "Tổ chức 1",
  fromDate: "29/08/2025",
  toDate: "29/08/2025",
  budget: "",
  program: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
  notes: "",
  purpose: "Tham gia chương trình đào tạo",
  country: "Việt Nam",
  otherPurpose: "",
};

const mockDelegationMembers: DelegationMember[] = [
  {
    id: "1",
    stt: 1,
    fullName: "Nguyễn Văn A",
    dateOfBirth: "15/03/1985",
    gender: "Nam",
    phone: "0123456789",
    passportNumber: "A1234567",
    expirationDate: "15/03/2030",
    address: "123 Đường ABC, Quận 1, TP.HCM",
    nationality: "Việt Nam",
    organization: "Viện Khoa học Thủy lợi",
    role: "Trưởng đoàn",
    department: "Phòng Kế hoạch khoa học",
  },
  {
    id: "2",
    stt: 2,
    fullName: "Trần Thị B",
    dateOfBirth: "22/07/1990",
    gender: "Nữ",
    phone: "0987654321",
    passportNumber: "B7654321",
    expirationDate: "22/07/2028",
    address: "456 Đường XYZ, Quận 2, TP.HCM",
    nationality: "Việt Nam",
    organization: "Viện Khoa học Thủy lợi",
    role: "Thành viên",
    department: "Phòng Nghiên cứu",
  },
];

const DelegationDetail: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"info" | "members">("info");
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] =
    useState<DelegationDetail>(mockDelegationDetail);
  const [members, setMembers] = useState<DelegationMember[]>(
    mockDelegationMembers
  );

  const handleInputChange = (field: keyof DelegationDetail, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMemberInputChange = (
    index: number,
    field: keyof DelegationMember,
    value: string
  ) => {
    setMembers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addMember = () => {
    const newMember: DelegationMember = {
      id: `new-${Date.now()}`,
      stt: members.length + 1,
      fullName: "",
      dateOfBirth: "",
      gender: "",
      phone: "",
      passportNumber: "",
      expirationDate: "",
      address: "",
      nationality: "Việt Nam",
      organization: "Viện Khoa học Thủy lợi",
      role: "",
      department: "",
    };
    setMembers([...members, newMember]);
  };

  const removeMember = (index: number) => {
    if (members.length > 1) {
      setMembers(members.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    // TODO: Implement save logic
    console.log("Saving delegation:", formData);
    console.log("Saving members:", members);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(mockDelegationDetail);
    setMembers(mockDelegationMembers);
    setIsEditing(false);
  };

  return (
    <PageShell title="Chi tiết đoàn">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/cooperation/incoming-delegations")}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">
              Chi tiết đoàn
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Chỉnh sửa</span>
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="bg-gray-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-gray-600 transition-colors flex items-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Hủy</span>
                </button>
                <button
                  onClick={handleSave}
                  className="bg-green-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>Lưu</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg ">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("info")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "info"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Thông tin đoàn
              </button>
              <button
                onClick={() => setActiveTab("members")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "members"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Thành phần đoàn
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "info" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tên đoàn:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.delegationName}
                          onChange={(e) =>
                            handleInputChange("delegationName", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập tên đoàn"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {formData.delegationName || "-"}
                        </p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tổ chức:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.organization}
                          onChange={(e) =>
                            handleInputChange("organization", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập tên tổ chức"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.organization}</p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Từ:
                      </label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={formData.fromDate}
                          onChange={(e) =>
                            handleInputChange("fromDate", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.fromDate}</p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ngân sách:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.budget}
                          onChange={(e) =>
                            handleInputChange("budget", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập ngân sách"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {formData.budget || "-"}
                        </p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chương trình:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.program}
                          onChange={(e) =>
                            handleInputChange("program", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập tên chương trình"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.program}</p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ghi chú:
                      </label>
                      {isEditing ? (
                        <textarea
                          value={formData.notes}
                          onChange={(e) =>
                            handleInputChange("notes", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={3}
                          placeholder="Nhập ghi chú"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.notes || "-"}</p>
                      )}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mục đích:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.purpose}
                          onChange={(e) =>
                            handleInputChange("purpose", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập mục đích"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.purpose}</p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quốc gia:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.country}
                          onChange={(e) =>
                            handleInputChange("country", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập quốc gia"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.country}</p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Đến:
                      </label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={formData.toDate}
                          onChange={(e) =>
                            handleInputChange("toDate", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900">{formData.toDate}</p>
                      )}
                    </div>

                    <div className="border-b border-gray-200 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mục đích khác:
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.otherPurpose}
                          onChange={(e) =>
                            handleInputChange("otherPurpose", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập mục đích khác"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {formData.otherPurpose || "-"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "members" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Thành phần đoàn
                  </h3>
                </div>

                {isEditing ? (
                  <div className="bg-white rounded-lg">
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-8">
                              STT
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-32">
                              Họ và tên
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-20">
                              Ngày sinh
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-12">
                              Giới tính
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-16">
                              Điện thoại
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-16">
                              Số hộ chiếu
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-20">
                              Ngày hết hạn
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-20">
                              Địa chỉ
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-16">
                              Quốc tịch
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-16">
                              Tổ chức
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-16">
                              Vai trò
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-left w-20">
                              Phòng ban/đơn vị
                            </th>
                            <th className="border border-gray-300 px-1 py-2 text-sm font-bold text-black text-center w-12">
                              Thao tác
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member, index) => (
                            <tr key={member.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-1 py-2 text-sm text-gray-900 text-center w-8">
                                {index + 1}
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-32">
                                <input
                                  type="text"
                                  value={member.fullName}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "fullName",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Họ và tên..."
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-20">
                                <input
                                  type="date"
                                  value={member.dateOfBirth}
                                  onChange={(e) => {
                                    const date = new Date(e.target.value);
                                    const formattedDate =
                                      date.toLocaleDateString("vi-VN");
                                    handleMemberInputChange(
                                      index,
                                      "dateOfBirth",
                                      formattedDate
                                    );
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-12">
                                <select
                                  value={member.gender}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "gender",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="">- Giới tính</option>
                                  <option value="Nam">Nam</option>
                                  <option value="Nữ">Nữ</option>
                                </select>
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-16">
                                <input
                                  type="text"
                                  value={member.phone}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "phone",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Điện thoại..."
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-16">
                                <input
                                  type="text"
                                  value={member.passportNumber}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "passportNumber",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Số hộ chiếu..."
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-20">
                                <input
                                  type="date"
                                  value={member.expirationDate}
                                  onChange={(e) => {
                                    const date = new Date(e.target.value);
                                    const formattedDate =
                                      date.toLocaleDateString("vi-VN");
                                    handleMemberInputChange(
                                      index,
                                      "expirationDate",
                                      formattedDate
                                    );
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-20">
                                <input
                                  type="text"
                                  value={member.address}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "address",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Địa chỉ"
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-16">
                                <select
                                  value={member.nationality}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "nationality",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="Việt Nam">Việt Nam</option>
                                  <option value="Mỹ">Mỹ</option>
                                  <option value="Nhật Bản">Nhật Bản</option>
                                  <option value="Hàn Quốc">Hàn Quốc</option>
                                </select>
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-16">
                                <input
                                  type="text"
                                  value={member.organization}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "organization",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-16">
                                <select
                                  value={member.role}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "role",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="">- Vai trò</option>
                                  <option value="Trưởng đoàn">
                                    Trưởng đoàn
                                  </option>
                                  <option value="Phó đoàn">Phó đoàn</option>
                                  <option value="Thành viên">Thành viên</option>
                                </select>
                              </td>
                              <td className="border border-gray-300 px-1 py-2 w-20">
                                <select
                                  value={member.department}
                                  onChange={(e) =>
                                    handleMemberInputChange(
                                      index,
                                      "department",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-1 py-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="">- Phòng ban/đơn vị</option>
                                  <option value="Phòng Kế hoạch">
                                    Phòng Kế hoạch
                                  </option>
                                  <option value="Phòng Tài chính">
                                    Phòng Tài chính
                                  </option>
                                  <option value="Phòng Nhân sự">
                                    Phòng Nhân sự
                                  </option>
                                </select>
                              </td>
                              <td className="border border-gray-300 px-1 py-2 text-center w-12">
                                <button
                                  type="button"
                                  onClick={() => removeMember(index)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Xóa thành viên"
                                  disabled={members.length === 1}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Add Member Button */}
                    <div className="p-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={addMember}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Thêm thành viên
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                              STT
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                              Họ và tên
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                              Ngày sinh
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                              Giới tính
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                              Điện thoại
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                              Số hộ chiếu
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                              Ngày hết hạn
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                              Địa chỉ
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                              Quốc tịch
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                              Tổ chức
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                              Vai trò
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-24">
                              Phòng ban/đơn vị
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {members.map((member) => (
                            <tr key={member.id} className="hover:bg-gray-50">
                              <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                                {member.stt}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                                <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                                  {member.fullName}
                                </div>
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                                {member.dateOfBirth}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                                {member.gender}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                                {member.phone}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                                {member.passportNumber}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                                {member.expirationDate}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                                <div className="leading-tight">
                                  {member.address}
                                </div>
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                                {member.nationality}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                                <div className="leading-tight">
                                  {member.organization}
                                </div>
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                                {member.role}
                              </td>
                              <td className="px-2 py-4 text-sm text-gray-900 w-24">
                                <div className="leading-tight">
                                  {member.department}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default DelegationDetail;
